/** biome-ignore-all lint/suspicious/noConsole: false positive */
import { Location } from '@cobliteam/common-types/src/location';
import type { Preferences } from '@cobliteam/common-types/src/preferences';
import { DatadogTrack } from '@cobliteam/datadog-rum';
import { useUser } from '@cobliteam/hooks';
import { useNewsSidebar } from '@cobliteam/hooks/src/useEntity/usePrismic';
import useFeatureFlags from '@cobliteam/hooks/src/useFeatureFlags';
import { useTranslation } from '@cobliteam/i18n';
import { PrismicImage, PrismicLink, PrismicRichText } from '@cobliteam/prismic';
import { Button, Divider, Popover, Tag, Tooltip, Typography } from '@cobliteam/ui/components';
import { PopoverContent, PopoverTrigger } from '@cobliteam/ui/components/Popover';
import { useLocalStorage } from '@cobliteam/ui/hooks';
import Icon from '@cobliteam/ui/icons';
import { ScrollArea } from '@radix-ui/react-scroll-area';
import qs from 'query-string';
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useHistory } from 'react-router-dom';
import { getAnimatedIcon } from '../../AppSidebar/components/animatedIcons';

const NewsFeedFallback = () => {
  return <Button variant='surface' color='danger' size='sm' icon={<Icon name='x' />} aria-label='Ver novidades' />;
};

const NewsFeedContent = () => {
  const history = useHistory();
  const { session } = useUser();
  const { t } = useTranslation('common');
  const [isOpen, setIsOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const { data: news, content: sidebar, isLoading } = useNewsSidebar();
  const [preferences, setPreferences] = useLocalStorage<Preferences>('preferences');
  const { wrapper2025 } = useFeatureFlags();
  const [retrospection2025Data, setRetrospection2025Data] = useState<any>(null);

  const retrospection2025Url = useMemo(() => {
    const newFleetData = {
      ...retrospection2025Data,
      id: retrospection2025Data?.fleet_id,
    };

    // biome-ignore lint/performance/noDelete: <explanation>
    delete newFleetData?.fleet_id;

    return `https://main.dpvupxdvtpphd.amplifyapp.com?${qs.stringify(newFleetData)}`;
  }, [retrospection2025Data]);

  const fetchRetrospection2025Data = useCallback(async () => {
    try {
      const IFRAME_ORIGIN = 'https://main.dpvupxdvtpphd.amplifyapp.com';
      const response = await fetch(`${IFRAME_ORIGIN}/api/fleet`, {
        credentials: 'include',
        headers: {
          Authorization: `Bearer ${session?.authenticationToken}`,
        },
      });
      const data = await response.json();
      if (data?.fleet_id) setRetrospection2025Data(data);
    } catch (error: any) {
      console.error({ error });
    }
  }, [session?.authenticationToken]);

  useEffect(() => {
    if (session?.authenticationToken) fetchRetrospection2025Data();
  }, [session?.authenticationToken, fetchRetrospection2025Data]);

  if (isLoading && !sidebar && !news) return null;
  if (news?.length === 0) return null;

  // Verify if there are new news since the last time the news feed was opened
  const sidebarLastUpdatedTimestamp = sidebar?.last_publication_date ? new Date(sidebar.last_publication_date).getTime() : 0;
  const lastNewsFeedOpenedTimestamp = preferences?.lastNewsFeedOpenedDate ? Number(preferences.lastNewsFeedOpenedDate) : 0;
  const hideNewBadge = sidebarLastUpdatedTimestamp >= lastNewsFeedOpenedTimestamp;

  const handleOpenNewsFeed = (open: boolean) => {
    setIsOpen(open);
    if (open) setPreferences({ ...preferences, lastNewsFeedOpenedDate: sidebarLastUpdatedTimestamp + 1 });
  };

  return (
    <Popover open={isOpen} onOpenChange={handleOpenNewsFeed}>
      <Tooltip content={t('news_feed.title')} asChild position='right'>
        <PopoverTrigger asChild onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}>
          <div className='relative mt-1.5'>
            <Button
              data-testid='app-header-newslist-button'
              className='text-default hover:text-brand'
              variant={isOpen || hideNewBadge ? 'surface' : 'ghost'}
              color='primary'
              size='sm'
              icon={getAnimatedIcon({ icon: 'gift', isHovered })}
              aria-label='Ver novidades'
            />
            {hideNewBadge && (
              <span className='-top-0.5 -right-0.5 absolute flex size-2'>
                <span className='absolute inline-flex h-full w-full animate-ping rounded-full bg-brand opacity-75' />
                <span className='relative inline-flex size-2 rounded-full bg-brand' />
              </span>
            )}
          </div>
        </PopoverTrigger>
      </Tooltip>
      <PopoverContent align='end' side='right' showArrow={false} className='flex w-96 flex-col gap-4 overflow-y-auto border-gray-6 px-0 pt-4 pb-2 shadow-xl'>
        <ScrollArea className='max-h-[75vh]'>
          {retrospection2025Data && wrapper2025 && (
            <div className='mb-4 flex flex-col gap-2 px-5'>
              <Tag label='Retrospectiva 2025' variant='outlined' />
              <Typography variant='heading-lg' className='font-semibold text-primary'>
                Retrospectiva 2025 da sua frota já está disponível
              </Typography>
              <Typography variant='text-md' className='text-default text-gray-9 text-sm'>
                Confira os principais marcos da sua operação em 2025 e veja como seus indicadores de segurança se comparam com a base Cobli.
              </Typography>
              <a href={retrospection2025Url} target='_blank' rel='noopener'>
                <img src='/static/images/wrapper.gif' alt='Retrospectiva 2025' className='h-auto w-full rounded-lg' />
              </a>
            </div>
          )}
          {news?.map(({ data: { title, resume, image, link, type }, uid }: any) => (
            <Fragment key={uid}>
              <div className='flex flex-col gap-2 px-5'>
                <Tag label={type} variant='outlined' />
                <PrismicRichText field={title} className='font-semibold text-primary' />
                <PrismicRichText field={resume} className='text-default text-gray-9 text-sm' />
                <PrismicLink field={link} target='_blank'>
                  <PrismicImage field={image} className='h-40 w-full rounded-md border border-gray-4 object-contain shadow-md' widths={[342, 640]} imgixParams={{ q: 75 }} />
                </PrismicLink>
              </div>
              <Divider className='mt-10 mb-6 bg-gray-6' />
            </Fragment>
          ))}

          <div className='flex items-center justify-end pb-6'>
            <Button variant='link' size='md' onClick={() => history.push(Location.NEWS)}>
              Ver todas as notícias <Icon name='arrow-up-right' className='ml-1' size='xs' />
            </Button>
          </div>
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
};

const NewsFeed = () => {
  return (
    <ErrorBoundary
      fallback={<NewsFeedFallback />}
      onError={(error, errorInfo) => {
        DatadogTrack.error(error, {
          component: 'NewsFeed',
          section: 'AppHeader',
          errorInfo,
        });
      }}
    >
      <NewsFeedContent />
    </ErrorBoundary>
  );
};

export default NewsFeed;
